import {
  Component,
  Injector,
  Output,
  EventEmitter,
  Signal,
  WritableSignal,
  signal,
  effect,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import {
  Icons,
  DsButtonVariants,
  DsButtonIntent,
  DsTooltipPosition,
  IconScale,
  IconStyles,
  DsGradients,
} from '@levelaccess/design-system';
import { Subscription } from 'rxjs';
import { IChatSession } from '../../../../../shared/interfaces/ai-chat.interface';
import { AiChatBaseComponent } from '../ai-chat-base/ai-chat-base.component';
import { AiChatDeleteService } from '../ai-chat-delete.service';
import { IChatMessage } from '../ai-chat-base/ai-chat.interface';
import { AiChatHistoryComponent } from '../ai-chat-history/ai-chat-history.component';
import { DomUtility } from '../../../utility/dom.utility';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { VerticalNavCollapseOverrideService } from '../../../services/vertical-nav-collapse-override.service';

@Component({
  selector: 'app-ai-chat-panel',
  templateUrl: './ai-chat-panel.component.html',
  styleUrls: ['./ai-chat-panel.component.scss'],
  standalone: false,
})
export class AiChatPanelComponent extends AiChatBaseComponent implements AfterViewChecked {
  @Output() public closePanel: EventEmitter<void> = new EventEmitter<void>();
  @Output() public expandPanel: EventEmitter<void> = new EventEmitter<void>();
  @Output() public collapsePanel: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('panelBody', { static: false }) private panelBodyRef: ElementRef<HTMLDivElement>;
  @ViewChild(AiChatHistoryComponent) private historyComponent?: AiChatHistoryComponent;

  protected readonly Icons: typeof Icons = Icons;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly DsButtonIntent: typeof DsButtonIntent = DsButtonIntent;
  protected readonly DsTooltipPosition: typeof DsTooltipPosition = DsTooltipPosition;
  protected readonly IconScale: typeof IconScale = IconScale;
  protected readonly IconStyles: typeof IconStyles = IconStyles;
  protected readonly DsGradients: typeof DsGradients = DsGradients;

  private aiChatDeleteService: AiChatDeleteService;
  private verticalNavCollapseOverrideService: VerticalNavCollapseOverrideService;
  readonly isExpanded: Signal<boolean>;
  readonly showChatHistory: Signal<boolean>;
  readonly showExpandedHistory: WritableSignal<boolean>;
  readonly showCombinedChatPanel: Signal<boolean>;
  readonly showUxImprovements: Signal<boolean>;
  private pendingScrollPercentage: number | null = null;
  private deleteSubscription: Subscription = new Subscription();

  constructor(injector: Injector) {
    super(injector);
    this.aiChatDeleteService = this.injector.get(AiChatDeleteService);
    this.verticalNavCollapseOverrideService = this.injector.get(VerticalNavCollapseOverrideService);
    this.isExpanded = toSignal(this.aiChatStateService.showExpandedPanel$, { initialValue: false });
    this.showChatHistory = toSignal(this.aiChatStateService.showChatHistory$, { initialValue: false });
    this.showCombinedChatPanel = toSignal(
      this.injector.get(FeatureFlagService).variation$(FeatureFlagCollection.aiChatCombinedChatPanel, false).pipe(map(Boolean)),
      { initialValue: false },
    );
    this.showUxImprovements = toSignal(
      this.injector.get(FeatureFlagService).variation$(FeatureFlagCollection.aiChatUxImprovements, false).pipe(map(Boolean)),
      { initialValue: this.injector.get(FeatureFlagService).variation(FeatureFlagCollection.aiChatUxImprovements, false) },
    );
    this.showExpandedHistory = signal(!this.showUxImprovements());

    effect(() => this.showExpandedHistory.set(!this.showUxImprovements()), { allowSignalWrites: true });
    effect(() => {
      const isHistorySidebarVisible = this.isExpanded() && this.showUxImprovements() && this.showExpandedHistory();
      this.verticalNavCollapseOverrideService.setForceCollapsed(isHistorySidebarVisible);
    });
    this.deleteSubscription.add(
      this.verticalNavCollapseOverrideService.navExpanded$.subscribe(() => {
        if (this.showUxImprovements()) {
          this.showExpandedHistory.set(false);
        }
      }),
    );
  }

  protected override getScrollContainer(): HTMLElement | null {
    return this.panelBodyRef?.nativeElement || null;
  }

  public override onRatingChange(message: IChatMessage, rating: number): void {
    const scrollContainer = this.getScrollContainer();
    this.pendingScrollPercentage = scrollContainer ? DomUtility.getScrollPercentage(scrollContainer) : null;

    super.onRatingChange(message, rating);
  }

  public ngAfterViewChecked(): void {
    if (this.pendingScrollPercentage !== null) {
      const scrollContainer = this.getScrollContainer();
      if (scrollContainer) {
        setTimeout(() => {
          if (scrollContainer && this.pendingScrollPercentage !== null) {
            DomUtility.restoreScrollPosition(scrollContainer, this.pendingScrollPercentage);
          }
          this.pendingScrollPercentage = null;
        }, 10);
      } else {
        this.pendingScrollPercentage = null;
      }
    }
  }

  public onToggleChatHistory(): void {
    this.aiChatStateService.toggleChatHistory();
  }

  public onToggleExpandedHistory(): void {
    this.showExpandedHistory.update((visible: boolean) => !visible);
  }

  public onChatsClick(): void {
    if (this.isExpanded()) {
      this.onToggleExpandedHistory();
    } else {
      this.onToggleChatHistory();
    }
  }

  public onSessionSelected(session: IChatSession): void {
    this.loadSession(session.sessionId);
    this.aiChatStateService.hideChatHistory();
  }

  public onNewChat(): void {
    this.startNewChat();
    this.aiChatStateService.hideChatHistory();
  }

  public onClose(): void {
    this.aiChatStateService.closeAiChatPanel();
    this.closePanel.emit();
  }

  public onExpand(): void {
    this.aiChatStateService.expandAIChatPanel();
    if (!this.showUxImprovements()) {
      this.showExpandedHistory.set(true);
    }
    this.expandPanel.emit();
  }

  public onCollapse(): void {
    this.aiChatStateService.collapseAIChatPanel();
    this.collapsePanel.emit();
  }

  public onSessionDeleted(sessionId: string): void {
    if (sessionId === this.sessionId) {
      this.startNewChat();
    }
  }

  public onDeleteCurrentSession(): void {
    this.deleteSubscription.add(
      this.aiChatDeleteService.confirmAndDeleteSession(this.sessionId).subscribe({
        next: (): void => {
          this.startNewChat();
          this.historyComponent?.refresh();
        },
      }),
    );
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.deleteSubscription.unsubscribe();
    this.verticalNavCollapseOverrideService.setForceCollapsed(false);
  }
}
